import { env } from '@/lib/env'
import { landingApiRequest } from '@/lib/landing-api'
import { normalizePublicRouletteDisplay } from '@/lib/normalizePublicRouletteDisplay'

export type LandingRoulettePrizeType = 'coupon' | 'lucky_number' | 'pix' | 'none'

export type PublicRoulettePrizeDisplay = {
  label?: string | null
  /** Texto livre do cupom (preview / fatia quando não há percentual ou valor fixo) */
  description?: string | null
  discount_percent?: number | null
  discount_amount?: number | null
  min_purchase?: number | null
  quantity?: number | null
  amount?: number | null
}

export type PublicRoulettePrize = {
  position: number
  prize_type: LandingRoulettePrizeType
  display: PublicRoulettePrizeDisplay
}

export type PublicRouletteDisplay = {
  positions: number
  prizes: PublicRoulettePrize[]
}

export type LandingRouletteSpinsResponse = {
  available_spins: number
  shares_available: number
  paid_purchases: number
  shares_used: number
  spins_per_share: number
  roulette_unlocked?: boolean
  achievement_spins_earned?: boolean
}

export type LandingRouletteShareContext = {
  share_url: string
  share_title: string
  share_description: string
}

export type LandingRouletteShareResponse = {
  spins_added: number
  already_shared: boolean
  shares_available: number
}

export type LandingSpinResult = {
  position: number
  prize_type: LandingRoulettePrizeType
  prize_payload: Record<string, unknown> | null
}

export type LandingRouletteHistoryItem = {
  status?: 'pending' | 'used' | 'paid' | 'no_prize' | 'canceled'
  id: number
  prize_type: LandingRoulettePrizeType
  prize_payload:
    | (PublicRoulettePrizeDisplay & {
        code?: string
        lucky_numbers?: string[]
        valid_until?: string
        source?: 'scratch_card' | 'purchase_scratch_card' | 'achievement'
        achievement_id?: string
      })
    | null
  created_at: string
}

/**
 * Estrutura pública da roleta (sem probabilidades). 404 quando não há config.
 */
export async function fetchPublicCampaignRoulette(
  campaignId: number
): Promise<PublicRouletteDisplay | null> {
  const baseUrl = env.apiBaseUrl.replace(/\/$/, '')
  const url = `${baseUrl}/api/public/campaigns/${campaignId}/roulette`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })

    if (response.status === 404) return null

    const data = (await response.json()) as {
      success?: boolean
      data?: PublicRouletteDisplay
    }

    if (!response.ok || data.success === false || !data.data) {
      return null
    }

    return normalizePublicRouletteDisplay(data.data)
  } catch {
    return null
  }
}

export async function landingRouletteGetSpins(
  token: string,
  campaignId: number
): Promise<LandingRouletteSpinsResponse> {
  return landingApiRequest<LandingRouletteSpinsResponse>(
    `/api/campaigns/${campaignId}/roulette/spins`,
    {
      method: 'GET',
      token,
    }
  )
}

export async function landingRouletteGetShareContext(
  token: string,
  campaignId: number
): Promise<LandingRouletteShareContext> {
  return landingApiRequest<LandingRouletteShareContext>(
    `/api/campaigns/${campaignId}/share-context`,
    {
      method: 'GET',
      token,
    }
  )
}

export async function landingRouletteSpin(
  token: string,
  campaignId: number
): Promise<LandingSpinResult> {
  return landingApiRequest<LandingSpinResult>(
    `/api/campaigns/${campaignId}/roulette/spin`,
    {
      method: 'POST',
      token,
    }
  )
}

export async function landingRouletteShare(
  token: string,
  campaignId: number
): Promise<LandingRouletteShareResponse> {
  return landingApiRequest<LandingRouletteShareResponse>(
    `/api/campaigns/${campaignId}/roulette/share`,
    {
      method: 'POST',
      token,
    }
  )
}

export async function landingFetchRouletteHistory(
  token: string,
  campaignId: number
): Promise<LandingRouletteHistoryItem[]> {
  const response = await landingApiRequest<{ prizes: LandingRouletteHistoryItem[] }>(
    `/api/campaigns/${campaignId}/roulette/my-prizes`,
    {
      method: 'GET',
      token,
    }
  )

  return response.prizes ?? []
}

export async function landingRouletteUnlockShare(
  token: string,
  campaignId: number
): Promise<{ roulette_unlocked: boolean }> {
  return landingApiRequest('/api/campaigns/' + campaignId + '/roulette/unlock-share', {
    method: 'POST',
    token,
  })
}

export async function landingRouletteClaim(
  token: string,
  campaignId: number,
  prizeId: number
): Promise<LandingRouletteHistoryItem> {
  return landingApiRequest(
    '/api/campaigns/' + campaignId + '/roulette/prizes/' + prizeId + '/claim',
    { method: 'POST', token }
  )
}
